import React, { useEffect } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import Navbar from './BasicComponents/Navbar';
import AppRoutes from './Routes/routes';
import { UserProvider } from './BasicComponents/UserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './Context/SubscriptionContext';
import { SidebarProvider } from './Context/SidebarContext';

import { setupAxiosInterceptors } from './utils/axiosInterceptor';
import ScrollToTop from './utils/ScrollToTop';
import './Styles/tenant-theme.css';

import ErrorBoundary from './error-framework/ErrorBoundary';
import TsgAiBotGate from './components/TsgAiBot/TsgAiBotGate';
import { ErrorProvider } from './error-framework/ErrorContext';
import ErrorExperience from './error-framework/ErrorExperience';
import { OfflineBanner } from './error-framework/ErrorStates';

// Inner component to access navigate hook
const AppContent = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Setup axios interceptors with navigate function
    setupAxiosInterceptors(navigate);
  }, [navigate]);

  // Broadcast-only: dispatching an event cannot change any existing
  // behaviour, because nothing is required to listen for it. Screens that
  // want automatic offline recovery can opt in with a single useEffect.
  const handleReconnect = React.useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('tsg:reconnected'));
    } catch (e) {
      /* recovery signalling must never throw */
    }
  }, []);

  return (
    // ErrorProvider needs to be inside BrowserRouter (it reads the current
    // route via useLocation for context-aware messages) — see
    // error-framework/ErrorContext.jsx.
    <ErrorProvider>
      {/*
        Renders nothing while online, so there is no layout or paint cost
        on the happy path. On reconnect it fires a single app-wide event
        that data-loading screens can listen for to refetch themselves,
        which is what makes recovery automatic rather than a manual refresh.
      */}
      <OfflineBanner onReconnect={handleReconnect} />
      <ScrollToTop />
      <Navbar />
      <AppRoutes />
      {/* Tee Gee — public visitor AI guide; hides itself on app/admin routes */}
      <TsgAiBotGate />
      <ErrorExperience />
    </ErrorProvider>
  );
};

function App() {
  return (
    // Top-level safety net: previously, an uncaught render error anywhere
    // in the tree produced a blank white screen. ErrorBoundary is purely
    // additive — it only activates on an actual crash.
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <UserProvider>
            <SubscriptionProvider>
              <SidebarProvider>
                <AppContent />
              </SidebarProvider>
            </SubscriptionProvider>
          </UserProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
